import { useEffect, useState, useMemo } from "react";
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  deleteDoc, 
  doc, 
  updateDoc 
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { 
  Trash2, 
  Search, 
  Filter, 
  Download, 
  Star, 
  MessageSquare,
  X,
  Edit2,
  Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { migrateOldReviews } from "./migrateOldReviews";

export default function AdminFeedbackPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("All"); 
  
  // Modals
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReviews(list);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Filtered Data
  const filteredReviews = useMemo(() => {
    return reviews.filter((review) => {
      const matchesSearch = 
        review.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        review.otherFeedback?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRating = ratingFilter === "All" || Number(review.overallExperience) === parseInt(ratingFilter);
      return matchesSearch && matchesRating;
    });
  }, [reviews, searchTerm, ratingFilter]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      "Date", "Name", "Overall Experience", "Channel", "Feedback", 
      "Cultural Programs", "Organization", "Cleanliness", "Parking", 
      "Hospitality", "Volunteers", "Crowd Mgmt", "Food/Prasada", 
      "Volunteer Scope", "Meaningful Program"
    ];
    
    const escapeCSV = (str) => `"${(str || "").toString().replace(/"/g, '""')}"`;

    const csvRows = filteredReviews.map(r => {
      const date = r.createdAt?.toDate?.().toLocaleDateString("en-IN") || "N/A";
      return [
        date,
        escapeCSV(r.name),
        r.overallExperience || "N/A",
        escapeCSV(r.communicationChannel),
        escapeCSV(r.otherFeedback),
        r.culturalPrograms || "N/A",
        r.organization || "N/A",
        r.cleanliness || "N/A",
        r.parking || "N/A",
        r.hospitality || "N/A",
        r.volunteers || "N/A",
        r.crowdManagement || "N/A",
        r.foodPrasada || "N/A",
        r.volunteerImprovement || "N/A",
        r.meaningfulProgram || "N/A",
      ].join(",");
    });

    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Devotee_Feedback_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "reviews", id));
      setDeleteConfirmId(null);
    } catch (error) {
      console.error("Error deleting review:", error);
      alert("Failed to delete review.");
    }
  };

  const handleUpdate = async () => {
    if (!editForm.name.trim()) return alert("Name is required.");
    
    try {
      setIsUpdating(true);
      const reviewRef = doc(db, "reviews", editForm.id);
      await updateDoc(reviewRef, {
        name: editForm.name.trim(),
        overallExperience: editForm.overallExperience,
        otherFeedback: editForm.otherFeedback.trim()
      });
      setEditForm(null);
    } catch (error) {
      console.error("Error updating review:", error);
      alert("Failed to update review.");
    } finally {
      setIsUpdating(false);
    }
  };

  const renderStars = (count, active = false) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={14}
            strokeWidth={i < count ? 0 : 1.5}
            className={i < count ? "fill-[#D4AF37] text-[#D4AF37]" : "fill-transparent text-[#E8DCC4]"}
          />
        ))}
      </div>
    );
  };

  const inputClass = "w-full rounded-xl border border-[#E8DCC4] bg-white/60 px-4 py-2.5 pl-10 text-sm text-[#2a0b06] outline-none transition-all placeholder:text-[#2a0b06]/40 focus:border-[#D4AF37] focus:bg-white focus:ring-4 focus:ring-[#D4AF37]/10";

  return (
    <div className="h-[100dvh] w-full bg-[#FCF8F2] font-sans text-[#2a0b06] flex flex-col overflow-hidden relative z-10">
      <style>{`
        .luxury-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .luxury-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .luxury-scrollbar::-webkit-scrollbar-thumb { background: #E8DCC4; border-radius: 10px; }
        .luxury-scrollbar::-webkit-scrollbar-thumb:hover { background: #D4AF37; }
      `}</style>

      {/* Ambient Background */}
      <div className="absolute top-10 -left-40 w-[560px] h-[560px] rounded-full bg-[#D4AF37]/10 blur-[130px] pointer-events-none -z-10" />
      <div className="absolute bottom-0 -right-32 w-[460px] h-[460px] rounded-full bg-[#722013]/10 blur-[110px] pointer-events-none -z-10" />

      {/* Fixed Header & Toolbar Area */}
      <div className="shrink-0 bg-white/70 backdrop-blur-xl border-b border-[#E8DCC4]/50 px-6 py-6 shadow-sm z-20">
        <div className="max-w-[1400px] mx-auto flex flex-col gap-6">
          
          {/* Header Row */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="mb-1 block text-[0.65rem] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
                Admin Dashboard
              </span>
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#2a0b06] tracking-tight">
                Feedback Management
              </h1>
            </div>
            
            <button
              onClick={handleExportCSV}
              className="group flex items-center justify-center gap-2 rounded-xl border border-[#2a0b06] bg-[#2a0b06] px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-[#FCF8F2] transition-all hover:bg-[#1a0704] hover:shadow-lg hover:shadow-[#2a0b06]/20"
            >
              <Download size={14} className="transition-transform group-hover:-translate-y-0.5 text-[#D4AF37]" />
              Export Data
            </button>

            <button
              onClick={migrateOldReviews}
              className="rounded-xl bg-red-600 px-4 py-3 text-white"
            >
              Migrate Old Reviews
            </button>
          </div>

          {/* Filters Toolbar */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#722013]/50" size={16} />
              <input
                type="text"
                placeholder="Search feedback by name or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={inputClass}
              />
            </div>

            <div className="relative md:w-56 shrink-0">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-[#722013]/50" size={16} />
              <select
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
                className={`${inputClass} appearance-none cursor-pointer pr-10`}
              >
                <option value="All">All Experiences</option>
                <option value="5">5 Stars - Excellent</option>
                <option value="4">4 Stars - Good</option>
                <option value="3">3 Stars - Average</option>
                <option value="2">2 Stars - Poor</option>
                <option value="1">1 Star - Critical</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto luxury-scrollbar p-4 sm:p-6 z-10">
        <div className="max-w-[1400px] mx-auto h-full flex flex-col">
          {loading ? (
            <div className="flex flex-1 items-center justify-center text-xs font-bold tracking-[0.2em] uppercase text-[#722013]/60">
              Loading records...
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-[2rem] border border-dashed border-[#E8DCC4] bg-white/40 text-center p-8 backdrop-blur-sm">
              <MessageSquare size={36} strokeWidth={1.5} className="text-[#D4AF37] mb-4" />
              <h3 className="font-serif text-2xl font-bold text-[#2a0b06] mb-2">No records found</h3>
              <p className="text-sm text-[#722013]/70">Try adjusting your search terms or filters.</p>
            </div>
          ) : (
            <>
              {/* DESKTOP VIEW: Clean Table */}
              <div className="hidden lg:flex flex-col bg-white/80 backdrop-blur-xl border border-[#E8DCC4]/60 shadow-lg shadow-[#722013]/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-[#2a0b06]">
                    <thead className="bg-[#FCF8F2]/80 border-b border-[#E8DCC4]/60">
                      <tr>
                        <th className="px-6 py-4 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-[#722013]">Date</th>
                        <th className="px-6 py-4 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-[#722013]">Devotee</th>
                        <th className="px-6 py-4 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-[#722013]">Experience</th>
                        <th className="px-6 py-4 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-[#722013] w-2/5">Reflections</th>
                        <th className="px-6 py-4 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-[#722013] text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8DCC4]/40">
                      <AnimatePresence>
                        {filteredReviews.map((review) => (
                          <motion.tr
                            layout
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            key={review.id}
                            className="hover:bg-[#FCF8F2]/50 transition-colors"
                          >
                            <td className="px-6 py-5 whitespace-nowrap text-xs font-semibold text-[#722013]/60">
                              {review.createdAt?.toDate?.().toLocaleDateString("en-IN", {
                                day: "2-digit", month: "short", year: "numeric"
                              })}
                            </td>
                            <td className="px-6 py-5">
                              <div className="font-serif text-lg font-bold text-[#2a0b06]">
                                {review.name}
                              </div>
                              {review.communicationChannel && (
                                <div className="text-[0.6rem] font-bold uppercase tracking-widest text-[#D4AF37] mt-1">
                                  Via {review.communicationChannel}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              {renderStars(Number(review.overallExperience || 0))}
                            </td>
                            <td className="px-6 py-5">
                              <p className="line-clamp-2 text-[#2a0b06]/80 text-[0.9rem] leading-relaxed">
                                {review.otherFeedback || <span className="text-[#2a0b06]/30 italic">No written feedback provided.</span>}
                              </p>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setEditForm(review)}
                                  className="inline-flex p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors"
                                  title="Edit Record"
                                >
                                  <Edit2 size={16} strokeWidth={2.5} />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmId(review.id)}
                                  className="inline-flex p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete Record"
                                >
                                  <Trash2 size={16} strokeWidth={2.5} />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* MOBILE / TABLET VIEW: Minimalist Cards */}
              <div className="grid lg:hidden gap-4">
                <AnimatePresence>
                  {filteredReviews.map((review) => (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      key={review.id}
                      className="bg-white/80 backdrop-blur-xl p-5 sm:p-6 rounded-2xl border border-[#E8DCC4]/60 shadow-lg shadow-[#722013]/5 flex flex-col gap-4"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-serif text-lg font-bold text-[#2a0b06]">
                            {review.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-[0.6rem] font-bold uppercase tracking-widest text-[#722013]/60">
                              {review.createdAt?.toDate?.().toLocaleDateString("en-IN", {
                                day: "numeric", month: "short", year: "numeric"
                              })}
                            </p>
                            {review.communicationChannel && (
                              <>
                                <span className="text-[#E8DCC4]">•</span>
                                <span className="text-[0.6rem] font-bold uppercase tracking-widest text-[#D4AF37]">
                                  {review.communicationChannel}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 -mt-1 -mr-1">
                          <button
                            onClick={() => setEditForm(review)}
                            className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors"
                          >
                            <Edit2 size={16} strokeWidth={2.5} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(review.id)}
                            className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>

                      <div className="bg-[#FCF8F2] p-2.5 rounded-xl border border-[#E8DCC4]/40 inline-flex w-max">
                        {renderStars(Number(review.overallExperience || 0))}
                      </div>
                      
                      <p className="text-[#2a0b06]/80 text-[0.9rem] leading-relaxed">
                        {review.otherFeedback || <span className="text-[#2a0b06]/30 italic">No written feedback provided.</span>}
                      </p>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ------------------------- Edit Modal ------------------------- */}
      <AnimatePresence>
        {editForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#2a0b06]/40 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-full max-w-lg bg-white/95 backdrop-blur-xl p-1.5 shadow-2xl rounded-[2rem] border border-[#E8DCC4]"
            >
              <div className="bg-gradient-to-b from-[#FCF8F2] to-white rounded-[1.75rem] border border-[#E8DCC4]/40 p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-serif text-2xl font-bold text-[#2a0b06]">
                      Edit Feedback
                    </h3>
                    <p className="text-[0.65rem] font-bold uppercase tracking-widest text-[#D4AF37] mt-1">
                      Update devotee reflection
                    </p>
                  </div>
                  <button 
                    onClick={() => setEditForm(null)}
                    className="p-2 text-[#722013]/50 hover:text-[#722013] hover:bg-[#E8DCC4]/30 rounded-full transition-colors"
                  >
                    <X size={18} strokeWidth={2.5} />
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#722013]">
                      Devotee Name
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full border-b-2 border-[#E8DCC4] bg-transparent pb-2 text-lg font-serif text-[#2a0b06] outline-none transition-colors focus:border-[#D4AF37]"
                    />
                  </div>

                  <div>
                    <label className="mb-3 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#722013]">
                      Overall Experience
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setEditForm({ ...editForm, overallExperience: n })}
                          className={`flex h-10 w-10 items-center justify-center rounded-full border text-xs font-bold transition-all ${
                            editForm.overallExperience === n
                              ? "border-[#D4AF37] bg-[#D4AF37] text-[#2a0b06] shadow-md"
                              : "border-[#E8DCC4] bg-white text-slate-400 hover:border-[#D4AF37]"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#722013]">
                      Reflections
                    </label>
                    <textarea
                      rows="4"
                      value={editForm.otherFeedback}
                      onChange={(e) => setEditForm({ ...editForm, otherFeedback: e.target.value })}
                      className="w-full resize-none rounded-xl border border-[#E8DCC4] bg-white p-4 text-sm font-serif text-[#2a0b06] outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-8 pt-6 border-t border-[#E8DCC4]/50">
                  <button
                    onClick={() => setEditForm(null)}
                    className="flex-1 rounded-xl border border-[#E8DCC4] bg-white py-3 text-[10px] font-bold uppercase tracking-widest text-[#722013] hover:bg-[#FCF8F2] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    disabled={isUpdating}
                    className="flex-1 rounded-xl bg-[#2a0b06] py-3 text-[10px] font-bold uppercase tracking-widest text-[#FCF8F2] hover:bg-[#1a0704] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isUpdating ? "Saving..." : "Save Changes"} {!isUpdating && <Check size={14} />}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ------------------------- Minimalist Delete Confirmation Modal ------------------------- */}
      <AnimatePresence>
        {deleteConfirmId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#2a0b06]/40 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-full max-w-sm bg-white/95 backdrop-blur-xl p-1.5 shadow-2xl rounded-[2rem] border border-[#E8DCC4]"
            >
              <div className="bg-gradient-to-b from-[#FCF8F2] to-white rounded-[1.75rem] border border-[#E8DCC4]/40 p-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 border border-red-100 mb-5">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                
                <h3 className="font-serif text-2xl font-bold text-[#2a0b06] mb-2">
                  Remove Record?
                </h3>
                <p className="text-[#722013]/70 text-sm mb-8">
                  This action is permanent. The feedback entry will be completely removed from the database.
                </p>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="flex-1 rounded-xl border border-[#E8DCC4] bg-white py-3 text-[10px] font-bold uppercase tracking-widest text-[#722013] hover:bg-[#FCF8F2] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirmId)}
                    className="flex-1 rounded-xl bg-red-500 py-3 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}